/* eslint-disable react/prop-types */
'use client';

import type React from 'react';
import { useRef, useState } from 'react';
import { X, MessageCircle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/lib/ChatComponents/button';

export type ChatPosition = 'bottom-right' | 'bottom-left';
export type ChatSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const chatConfig = {
    dimensions: {
        sm: "bb:sm:max-w-sm bb:sm:max-h-[500px]",
        md: "bb:sm:max-w-md bb:sm:max-h-[600px]",
        lg: "bb:sm:max-w-lg bb:sm:max-h-[700px]",
        xl: "bb:sm:max-w-xl bb:sm:max-h-[800px]",
        full: "bb:sm:w-full bb:sm:h-full",
    },
    positions: {
        "bottom-right": "bb:bottom-5 bb:right-5",
        "bottom-left": "bb:bottom-5 bb:left-5",
    },
    chatPositions: {
        "bottom-right": "bb:sm:bottom-[calc(100%+10px)] bb:sm:right-0",
        "bottom-left": "bb:sm:bottom-[calc(100%+10px)] bb:sm:left-0",
    },
    states: {
        open: "bb:pointer-events-auto bb:opacity-100 bb:visible bb:scale-100 bb:translate-y-0",
        closed: "bb:pointer-events-none bb:opacity-0 bb:invisible bb:scale-100 bb:sm:translate-y-5",
    },
};

type ExpandableChatProps = {
    position?: ChatPosition;
    size?: ChatSize;
    icon?: React.ReactNode;
    isLoading?: boolean;
    progressPercentage?: number;
    onToggle?: (isOpen: boolean) => void;
} & React.HTMLAttributes<HTMLDivElement>;

const ExpandableChat: React.FC<ExpandableChatProps> = ({
    className,
    position = "bottom-right",
    size = "md",
    icon,
    isLoading = false,
    progressPercentage = 0,
    onToggle,
    children,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const chatRef = useRef<HTMLDivElement>(null);

    const toggleChat = () => {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        if (onToggle) {
            onToggle(newIsOpen);
        }
    };

    return (
        <div className={cn(`bb:box-border bb:fixed ${chatConfig.positions[position]} bb:z-50`, className)} {...props}>
            <div
                ref={chatRef}
                className={cn(
                    "bb:flex bb:flex-col bb:bg-background bb:border bb:sm:rounded-lg bb:shadow-md bb:overflow-hidden bb:transition-all bb:duration-250 bb:ease-out bb:sm:absolute bb:sm:w-[90vw] bb:sm:h-[80vh] bb:fixed bb:inset-0 bb:w-full bb:h-full bb:sm:inset-auto",
                    chatConfig.chatPositions[position],
                    chatConfig.dimensions[size],
                    isOpen ? chatConfig.states.open : chatConfig.states.closed,
                    className,
                )}
            >
                {children}
                <Button
                    variant="ghost"
                    size="icon"
                    className="bb:absolute bb:top-2 bb:right-2 bb:sm:hidden"
                    onClick={toggleChat}
                >
                    <X className="bb:h-4 bb:w-4" />
                </Button>
            </div>
            <ExpandableChatToggle
                icon={icon}
                isOpen={isOpen}
                isLoading={isLoading}
                progressPercentage={progressPercentage}
                toggleChat={toggleChat}
            />
        </div>
    );
};

ExpandableChat.displayName = "ExpandableChat";

const ExpandableChatHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div className={cn("bb:flex bb:items-center bb:justify-between bb:p-4 bb:border-b", className)} {...props} />
);

ExpandableChatHeader.displayName = "ExpandableChatHeader";

const ExpandableChatBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div className={cn("bb:flex-grow bb:overflow-y-auto", className)} {...props} />
);

ExpandableChatBody.displayName = "ExpandableChatBody";

const ExpandableChatFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div className={cn("bb:border-t bb:p-4", className)} {...props} />
);

ExpandableChatFooter.displayName = "ExpandableChatFooter";

type ExpandableChatToggleProps = {
    icon?: React.ReactNode;
    isOpen: boolean;
    isLoading?: boolean;
    progressPercentage?: number;
    toggleChat: () => void;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const ExpandableChatToggle: React.FC<ExpandableChatToggleProps> = ({
    className,
    icon,
    isOpen,
    isLoading = false,
    progressPercentage = 0,
    toggleChat,
    ...props
}) => {
    const clampedProgress = Math.max(0, Math.min(100, progressPercentage));

    return (
        <Button
            variant="default"
            onClick={toggleChat}
            className={cn(
                "bb:w-14 bb:h-14 bb:rounded-full bb:shadow-md bb:flex bb:items-center bb:justify-center hover:bb:shadow-lg hover:bb:shadow-black/30 bb:transition-all bb:duration-300 bb:relative bb:overflow-hidden",
                className,
            )}
            style={
                {
                    "--progress": `${100 - clampedProgress}%`,
                } as React.CSSProperties & { "--progress": string }
            }
            {...props}
        >
            {/* Progress fill overlay */}
            {isLoading && (
                <div
                    className="bb:absolute bb:inset-0 bb:bg-gradient-to-t bb:from-blue-500/30 bb:to-blue-400/20 bb:transition-transform bb:duration-300 bb:ease-out"
                    style={{
                        transform: `translateY(var(--progress))`,
                    }}
                />
            )}

            {/* Icon with z-index to stay above the fill */}
            <div className="bb:relative bb:z-10">
                {isOpen ? <X className="bb:h-6 bb:w-6" /> : icon || <MessageCircle className="bb:h-6 bb:w-6" />}
            </div>
        </Button>
    );
};

ExpandableChatToggle.displayName = 'ExpandableChatToggle';

export {
  ExpandableChat,
  ExpandableChatHeader,
  ExpandableChatBody,
  ExpandableChatFooter,
};
